import { apiGet, apiPost } from '@/lib/api';
import { Conversation, GroupMember } from '@/types';
import socketService from '@/lib/socket';

/**
 * 群聊服务模块
 * 提供群聊相关的 API 调用
 */

// ==================== 接口定义 ====================

export interface CreateGroupChatParams {
  users: Array<{
    userId: number;
    role: 'owner' | 'manager' | 'editor' | 'viewer';
  }>;
  projectId: string;
  title?: string;
}

export interface CreateGroupChatResponse {
  code: number;
  conversationId: string;
  message?: string;
}

export interface SendGroupMessageParams {
  conversationId: string;
  message: string;
  clientId?: number | null;
  fileIds?: number[];
  projectId?: string;
  sid?: string;
  apiType?: string;
}

export interface FetchGroupConversationsResponse {
  code: number;
  conversations?: any[];
  totalCount?: number;
  message?: string;
}

// ==================== API 方法 ====================

/**
 * 创建群聊
 * @param params 创建参数
 * @returns 创建结果，包含 conversationId
 */
export async function createGroupChat(
  params: CreateGroupChatParams
): Promise<CreateGroupChatResponse> {
  const response = await apiPost<CreateGroupChatResponse>(
    '/api/ai/conversations/group',
    {
      users: params.users,
      projectId: params.projectId,
      title: params.title || '',
    }
  );
  return response;
}

/**
 * 发送群聊消息
 * @param params 消息参数
 * @returns Promise<void>
 */
export async function sendGroupMessage(
  params: SendGroupMessageParams
): Promise<any> {
  const sid = params.sid || socketService.getSocketId();

  // 🔥 检查消息是否包含 @Agent 关键字
  const atAgentRegex = /@Agent\b/gi;
  const hasAtAgent = atAgentRegex.test(params.message);

  // 如果包含 @Agent，保留 clientId
  // 如果不包含 @Agent，不传 clientId（让后端使用默认逻辑）
  // 🔥 发送到后端时移除 @Agent 关键字（渲染时通过 client.id 判断是否显示 @Agent 标签）
  const finalMessage = params.message.replace(/@Agent\b\s*/gi, '').trim();
  const finalClientId = hasAtAgent ? params.clientId : null;

  const response = await apiPost('/api/clients/group_chat', {
    conversationId: params.conversationId,
    message: finalMessage,
    clientId: finalClientId,
    fileIds: params.fileIds || [],
    projectId: params.projectId,
    sid: sid,
    apiType: params.apiType,
  });

  return response;
}

/**
 * 获取群聊列表
 * @param page 页码，默认 1
 * @param pageSize 每页数量，默认 30
 * @returns 群聊会话列表
 */
export async function fetchGroupConversations(
  page: number = 1,
  pageSize: number = 30
): Promise<Conversation[]> {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));
  params.append('allProjects', 'true');
  params.append('conversationType', 'group');

  const response = await apiGet<FetchGroupConversationsResponse>(
    `/api/ai/conversations?${params.toString()}`
  );

  if (response.code !== 200) {
    return [];
  }

  const items = response.conversations || [];

  // 映射为 Conversation 类型
  const conversations: Conversation[] = items.map((conv: any) => ({
    id: conv.conversationId,
    conversationId: conv.conversationId,
    title: conv.title || 'Group Chat',
    treeData: [],
    versionPath: {},
    createdAt: conv.createdAt || new Date().toISOString(),
    updatedAt: conv.updatedAt || conv.createdAt || new Date().toISOString(),
    projectId: conv.projectId || null,
    starred: conv.starred || false,
    conversationType: 'group',
    members: conv.members || [],
    lastVisitedAt: conv.lastVisitedAt ?? null, // 🔥 保留 lastVisitedAt 用于未读计数
  }));

  return conversations;
}

/**
 * 获取群聊详情（包含成员列表）
 * @param conversationId 会话ID
 * @returns 群聊详情
 */
export async function getGroupChatDetail(
  conversationId: string
): Promise<{ members: GroupMember[] } | null> {
  try {
    const response = await apiGet<any>(
      `/api/ai/conversations/${conversationId}`
    );

    if (response.code === 200 && response.data) {
      return {
        members: response.data.members || [],
      };
    }
    return null;
  } catch {
    return null;
  }
}

// ==================== 群成员相关接口 ====================

export interface GroupUserInfo {
  id: number;
  name: string;
  email: string;
}

export interface GroupUserWithRole {
  user: GroupUserInfo;
  role: 'owner' | 'manager' | 'editor' | 'viewer';
}

export interface FetchGroupUsersResponse {
  code: number;
  message: string;
  users: GroupUserWithRole[];
}

/**
 * 获取群聊成员列表
 * @param conversationId 会话ID
 * @returns 群成员列表
 */
export async function fetchGroupUsers(
  conversationId: string
): Promise<GroupUserWithRole[]> {
  try {
    const response = await apiGet<FetchGroupUsersResponse>(
      `/api/ai/conversations/${conversationId}/users`
    );

    if (response.code === 200 && response.users) {
      return response.users;
    }
    return [];
  } catch (error) {
    console.error('Failed to fetch group users:', error);
    return [];
  }
}
